import { openobserveRum } from "@openobserve/browser-rum";
import type { PropagatorType } from "@openobserve/browser-rum";
import { openobserveLogs } from "@openobserve/browser-logs";

/**
 * Initializes OpenObserve RUM (Real User Monitoring) and browser logs.
 *
 * Additive, dev-only observability instrumentation. Safe to call even when
 * the client token is not configured (e.g. local dev, other environments) -
 * it becomes a no-op in that case.
 */
export function initRum(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    // OPENOBSERVE_CLIENT_TOKEN is a public, write-only RUM token injected at build time.
    const clientToken = process.env.REACT_APP_OPENOBSERVE_CLIENT_TOKEN;

    if (!clientToken) {
      return;
    }

    const allowedTracingUrls = [
      {
        match: (url: string) => {
          try {
            const { origin, hostname } = new URL(url);
            return origin === window.location.origin || hostname.endsWith(".incruiter.com");
          } catch {
            return false;
          }
        },
        propagatorTypes: ["tracecontext", "openobserve"] as PropagatorType[],
      },
    ];

    openobserveRum.init({
      applicationId: "frontend-feedback",
      clientToken,
      site: "monitor.incruiter.com",
      organizationIdentifier: "default",
      service: "frontend-feedback",
      env: "development",
      version: "0.0.1",
      insecureHTTP: false,
      apiVersion: "v1",
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: "mask",
      allowedTracingUrls,
    });

    openobserveRum.startSessionReplayRecording();

    openobserveLogs.init({
      clientToken,
      site: "monitor.incruiter.com",
      organizationIdentifier: "default",
      service: "frontend-feedback",
      env: "development",
      version: "0.0.1",
      insecureHTTP: false,
      apiVersion: "v1",
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  } catch (e) {
    console.warn("[RUM] Failed to initialize OpenObserve RUM", e);
  }
}
